import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Check, AlertCircle, ArrowRight, Shield, RefreshCw, Copy, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomDomainView = ({ businessProfile }) => {
  const [domain, setDomain] = useState('');
  const [savedDomain, setSavedDomain] = useState('');
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [copied, setCopied] = useState(false);

  const isEnterprise = ['enterprise', 'Enterprise'].includes(businessProfile?.subscription_tier);
  const cnameTarget = 'cname.altrunic.com';

  useEffect(() => {
    if (!businessProfile?.business_id || !isEnterprise) {
      setLoading(false);
      return;
    }
    const fetchDomain = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await axios.get(
          `${BACKEND_URL}/api/custom-domain/${businessProfile.business_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedDomain(res.data.custom_domain || '');
        setDomain(res.data.custom_domain || '');
        setVerified(res.data.verified || false);
      } catch {
        // Not enterprise or no domain set
      } finally {
        setLoading(false);
      }
    };
    fetchDomain();
  }, [businessProfile?.business_id, isEnterprise]);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/custom-domain/${businessProfile.business_id}`,
        { custom_domain: domain },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedDomain(res.data.custom_domain || '');
      setVerified(res.data.verified || false);
      setMessage(res.data.message);
      setMessageType('success');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to save domain');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setMessage('');
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/custom-domain/${businessProfile.business_id}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVerified(res.data.verified || false);
      setMessage(res.data.message);
      setMessageType(res.data.verified ? 'success' : 'warning');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Verification failed');
      setMessageType('error');
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyCname = () => {
    navigator.clipboard.writeText(cnameTarget);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
      </div>
    );
  }

  // Not Enterprise — show upgrade prompt
  if (!isEnterprise) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="custom-domain-upgrade">
        <div className="text-center max-w-md mx-auto">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Custom Domain</h3>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">
            Serve your impact portal on your own domain (e.g., <span className="font-mono text-gray-700">impact.yourcompany.com</span>). Available on the Enterprise plan.
          </p>
          <button
            data-testid="custom-domain-upgrade-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'billing' }))}
            className="mt-6 inline-flex items-center gap-2 bg-gray-900 text-white font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-gray-800 transition-all"
          >
            Upgrade to Enterprise
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-domain-view">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Custom Domain</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Serve your impact portal at your own domain instead of the default Altrunic URL.
            </p>
          </div>
          {verified && savedDomain && (
            <div className="ml-auto flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold">
              <Check className="w-3.5 h-3.5" />
              Verified
            </div>
          )}
        </div>
      </div>

      {/* Status messages */}
      {message && (
        <div className={`p-4 rounded-xl border text-sm font-medium ${
          messageType === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' :
          messageType === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-700' :
          'bg-red-50 border-red-100 text-red-700'
        }`} data-testid="custom-domain-message">
          {message}
        </div>
      )}

      {/* Domain Input */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-1.5">Your domain</label>
          <p className="text-xs text-gray-500 mb-3">Enter the subdomain where you want your impact portal to be accessible.</p>
          <div className="flex gap-3">
            <input
              data-testid="custom-domain-input"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="impact.yourcompany.com"
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none"
            />
            <button
              data-testid="custom-domain-save-btn"
              onClick={handleSave}
              disabled={saving || !domain.trim()}
              className="px-5 py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {savedDomain && (
          <div className="text-sm text-gray-600">
            Current domain: <span className="font-mono font-semibold text-gray-900">{savedDomain}</span>
            {verified ? (
              <span className="ml-2 text-emerald-600 font-medium">(Active)</span>
            ) : (
              <span className="ml-2 text-amber-600 font-medium">(Pending verification)</span>
            )}
          </div>
        )}
      </div>

      {/* DNS Setup Instructions */}
      {savedDomain && !verified && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h4 className="text-sm font-bold text-gray-900">DNS Configuration Required</h4>
            <p className="text-xs text-gray-500 mt-0.5">Add a CNAME record in your domain registrar (GoDaddy, Cloudflare, Namecheap, etc.)</p>
          </div>
          <div className="p-6 space-y-4">
            {/* DNS Record Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Type</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Host / Name</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Value / Points to</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-gray-700">TTL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3 font-mono text-gray-900 font-medium">CNAME</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{savedDomain.split('.')[0]}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-gray-900 font-medium">{cnameTarget}</span>
                        <button
                          onClick={handleCopyCname}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Copy"
                        >
                          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">Automatic</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Steps */}
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                <p className="text-gray-600">Go to your domain registrar's DNS settings (GoDaddy, Cloudflare, etc.)</p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                <p className="text-gray-600">Add a new <strong>CNAME</strong> record with the values shown above</p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                <p className="text-gray-600">Wait for DNS propagation (can take up to 48 hours, usually under 1 hour)</p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</div>
                <p className="text-gray-600">Click "Verify Domain" below to confirm the setup</p>
              </div>
            </div>

            {/* Verify button */}
            <button
              data-testid="custom-domain-verify-btn"
              onClick={handleVerify}
              disabled={verifying}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50"
            >
              {verifying ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Checking DNS...</>
              ) : (
                <><Shield className="w-4 h-4" /> Verify Domain</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Verified Success */}
      {savedDomain && verified && (
        <div className="bg-white rounded-xl border border-emerald-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-4 h-4 text-emerald-600" />
            </div>
            <h4 className="text-sm font-bold text-gray-900">Domain is live</h4>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Your impact portal is now accessible at:
          </p>
          <a
            href={`https://${savedDomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-mono font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            https://{savedDomain}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </div>
  );
};

export default CustomDomainView;
